import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "components/Button/Button";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { getWebsocketError } from "utils/NotificationManager/NotificationManager.selectors";
import { backendRetryDelayMs } from "utils/backendUnavailable";
import "./Error.scss";

interface BackendUnavailableProps {
    /** Re-run the failed fetch. The screen unmounts once the fetch succeeds. */
    onRetry: () => Promise<unknown> | unknown;
}

/**
 * Shown instead of the not-found screen when a record could not be loaded
 * because the backend is not available (502/503/504, timeout, network error).
 * Retries automatically with backoff, when the browser tab regains focus, and
 * when the live connection to the backend comes back; offers a Retry button.
 */
const BackendUnavailable = ({ onRetry }: BackendUnavailableProps) => {
    const [isRetrying, setRetrying] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(Math.round(backendRetryDelayMs(0) / 1000));
    const attemptRef = useRef(0);
    const retryingRef = useRef(false);
    const onRetryRef = useRef(onRetry);
    onRetryRef.current = onRetry;

    const retry = useCallback(async () => {
        if (retryingRef.current) {
            return;
        }
        retryingRef.current = true;
        setRetrying(true);
        try {
            await onRetryRef.current();
        } catch {
            // the next attempt is scheduled below; on success this screen unmounts
        } finally {
            retryingRef.current = false;
            attemptRef.current += 1;
            // Reset the countdown BEFORE clearing isRetrying: these updates are
            // not batched after an await, and a render with isRetrying=false and
            // secondsLeft=0 would fire a second retry at once.
            setSecondsLeft(Math.round(backendRetryDelayMs(attemptRef.current) / 1000));
            setRetrying(false);
        }
    }, []);

    // Countdown to the next automatic retry.
    useEffect(() => {
        if (isRetrying) {
            return;
        }
        if (secondsLeft <= 0) {
            retry();
            return;
        }
        const timer = window.setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
        return () => window.clearTimeout(timer);
    }, [secondsLeft, isRetrying, retry]);

    // Retry at once when the user comes back to the tab.
    useEffect(() => {
        const onFocus = () => {
            if (document.visibilityState === "visible") {
                retry();
            }
        };
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onFocus);
        return () => {
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onFocus);
        };
    }, [retry]);

    // Retry at once when the live connection to the backend recovers.
    const hasWebsocketError = useSelector(getWebsocketError);
    const hadWebsocketErrorRef = useRef(hasWebsocketError);
    useEffect(() => {
        if (hadWebsocketErrorRef.current && !hasWebsocketError) {
            retry();
        }
        hadWebsocketErrorRef.current = hasWebsocketError;
    }, [hasWebsocketError, retry]);

    return (
        <div className="error" data-testid="backend-unavailable">
            <div className="error__wrapper">
                <Icon icon={IconCollection.accessPointOff} className="error__icon" />
                <div className="error__content">
                    <h1 className="error__title">The server is not available right now.</h1>
                    <div className="error__subtitle">
                        This is usually a short interruption, for example during an update.{" "}
                        {isRetrying
                            ? "Retrying…"
                            : `Retrying automatically in ${secondsLeft} s.`}
                    </div>
                    <div className="error__actions">
                        <Button
                            variation="primary"
                            label="Retry now"
                            loading={isRetrying}
                            disabled={isRetrying}
                            onClick={() => retry()}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BackendUnavailable;
